/**
 * Componente de atualização de Configuração de Comissionamento
 *
 * @module App/CommissionConfig/Update
 */
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { Form } from "../shared/Form";
import {
  getShowCommissionConfig,
  updateCommissionConfigData,
} from "@/Api/commissionConfig";
import { CommissionConfigData } from "@/types/commissionConfig";
import { ApiDataProps } from "@/pages/Tenants";

type Props = {
  uuid: string;
  updateFront: (
    callback: (
      prev: ApiDataProps<CommissionConfigData>
    ) => ApiDataProps<CommissionConfigData>
  ) => void;
};

/**
 * Botão de atualização com modal
 */
export function UpdateBtn({ uuid, updateFront }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] =
    useState<CommissionConfigData | null>(null);

  /**
   * Busca dados do item a ser editado
   */
  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await getShowCommissionConfig(uuid);
      setInitialValues(data?.response);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const { data, ok } = await updateCommissionConfigData(uuid, values);

      if (ok) {
        toast.success(data?.message || "Configuração atualizada com sucesso!");

        updateFront((prev) => ({
          ...prev,
          data: prev.data.map((item) =>
            item.uuid === uuid ? data.response : item
          ),
        }));

        setOpen(false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[800px] overflow-y-auto max-h-[80vh]">
        {initialValues && (
          <Form
            title="Atualizar Configuração de Comissionamento"
            loading={loading}
            onSubmit={handleSubmit}
            buttonText="Atualizar"
            initialValues={initialValues}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
